import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Plus,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  Key,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Tenant {
  id: string;
  name: string;
  cnpj: string;
  tradeName?: string;
  email: string;
  phone?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  webhookUrl?: string;
  createdAt: string;
  _count?: {
    users: number;
    accounts: number;
  };
}

export function AdminTenants() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    tradeName: '',
    email: '',
    phone: '',
  });
  const [providerData, setProviderData] = useState({
    provider: 'OPENPIX',
    apiKey: '',
    webhookSecret: '',
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-tenants'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.tenants.list);
      return response.data as { tenants: Tenant[]; total: number };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post(ENDPOINTS.tenants.create, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
      const response = await api.put(ENDPOINTS.tenants.update(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      setIsModalOpen(false);
      setEditingTenant(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(ENDPOINTS.tenants.delete(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
    },
  });

  const configureProviderMutation = useMutation({
    mutationFn: async ({ tenantId, data }: { tenantId: string; data: typeof providerData }) => {
      const response = await api.post(ENDPOINTS.admin.providers(tenantId), data);
      return response.data;
    },
    onSuccess: () => {
      setIsProviderModalOpen(false);
      setSelectedTenantId(null);
      setProviderData({ provider: 'OPENPIX', apiKey: '', webhookSecret: '' });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      cnpj: '',
      tradeName: '',
      email: '',
      phone: '',
    });
  };

  const handleEdit = (tenant: Tenant) => {
    setEditingTenant(tenant);
    setFormData({
      name: tenant.name,
      cnpj: tenant.cnpj,
      tradeName: tenant.tradeName || '',
      email: tenant.email,
      phone: tenant.phone || '',
    });
    setIsModalOpen(true);
  };

  const handleConfigureProvider = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setIsProviderModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTenant) {
      updateMutation.mutate({ id: editingTenant.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleProviderSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTenantId) {
      configureProviderMutation.mutate({ tenantId: selectedTenantId, data: providerData });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.')) {
      deleteMutation.mutate(id);
    }
  };

  const tenants = data?.tenants || [];
  const filteredTenants = tenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.cnpj.includes(searchTerm) ||
    tenant.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar empresas</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Empresas (Tenants)</h1>
          <p className="text-surface-400">Gerencie as empresas cadastradas na plataforma</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setEditingTenant(null); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, CNPJ ou email..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Tenants List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary-400" />
            Lista de Empresas
            <Badge variant="info">{filteredTenants.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma empresa encontrada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredTenants.map(tenant => (
                <div
                  key={tenant.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-primary-400" />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{tenant.name}</p>
                      <p className="text-sm text-surface-500">
                        {tenant.tradeName && `${tenant.tradeName} • `}
                        CNPJ: {tenant.cnpj}
                      </p>
                      <p className="text-sm text-surface-500">{tenant.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-surface-400">
                        {tenant._count?.users || 0} usuários
                      </p>
                      <p className="text-surface-400">
                        {tenant._count?.accounts || 0} contas
                      </p>
                    </div>
                    <Badge variant={tenant.status === 'ACTIVE' ? 'success' : tenant.status === 'SUSPENDED' ? 'warning' : 'error'}>
                      {tenant.status}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleConfigureProvider(tenant.id)} title="Configurar Providers">
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(tenant)} title="Editar">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(tenant.id)} title="Excluir">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create/Edit Tenant Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingTenant(null); resetForm(); }}
        title={editingTenant ? 'Editar Empresa' : 'Nova Empresa'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Razão Social"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Nome Fantasia"
            value={formData.tradeName}
            onChange={(e) => setFormData({ ...formData, tradeName: e.target.value })}
          />
          <Input
            label="CNPJ"
            value={formData.cnpj}
            onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
            required
            disabled={!!editingTenant}
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label="Telefone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingTenant ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Configure Provider Modal */}
      <Modal
        isOpen={isProviderModalOpen}
        onClose={() => { setIsProviderModalOpen(false); setSelectedTenantId(null); }}
        title="Configurar Provider de Pagamento"
      >
        <form onSubmit={handleProviderSubmit} className="space-y-4">
          <Select
            label="Provider"
            value={providerData.provider}
            onChange={(e) => setProviderData({ ...providerData, provider: e.target.value })}
            options={[
              { value: 'OPENPIX', label: 'OpenPix (Woovi)' },
              { value: 'IUGU', label: 'iugu' },
              { value: 'ASAAS', label: 'Asaas' },
            ]}
          />
          <Input
            label="API Key / App ID"
            value={providerData.apiKey}
            onChange={(e) => setProviderData({ ...providerData, apiKey: e.target.value })}
            required
            type="password"
          />
          <Input
            label="Webhook Secret (opcional)"
            value={providerData.webhookSecret}
            onChange={(e) => setProviderData({ ...providerData, webhookSecret: e.target.value })}
            type="password"
          />
          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            <p className="font-medium text-surface-300 mb-1">Importante:</p>
            <p>As credenciais serão criptografadas e armazenadas de forma segura.</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsProviderModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={configureProviderMutation.isPending}>
              Salvar Configuração
            </Button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
}
